'use client';

import { useMemo } from 'react';

import { Label } from '@playbooks/ui/components/label';
import { Separator } from '@playbooks/ui/components/separator';
import { cn } from '@playbooks/ui/lib/utils';

import { type VariantProps, cva } from 'class-variance-authority';

function FieldSet({ className, ...props }: React.ComponentProps<'fieldset'>) {
	// Theme
	const rootClassName = cn(
		'flex flex-col gap-4 has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3',
		className,
	);

	// Render
	return <fieldset data-slot='field-set' className={rootClassName} {...props} />;
}

function FieldLegend({
	className,
	variant = 'legend',
	...props
}: React.ComponentProps<'legend'> & { variant?: 'legend' | 'label' }) {
	// Theme
	const rootClassName = cn(
		'mb-1.5 font-medium data-[variant=label]:text-sm data-[variant=legend]:text-base',
		className,
	);

	// Render
	return <legend data-slot='field-legend' data-variant={variant} className={rootClassName} {...props} />;
}

function FieldGroup({ className, ...props }: React.ComponentProps<'div'>) {
	// Theme
	const rootClassName = cn(
		'group/field-group @container/field-group flex w-full flex-col gap-5 data-[slot=checkbox-group]:gap-3 *:data-[slot=field-group]:gap-4',
		className,
	);

	// Render
	return <div data-slot='field-group' className={rootClassName} {...props} />;
}

const fieldVariants = cva('group/field mb-3 flex w-full gap-2 last:mb-0 data-[invalid=true]:text-danger', {
	variants: {
		orientation: {
			vertical: 'flex-col *:w-full [&>.sr-only]:w-auto',
			horizontal:
				'flex-row items-center has-[>[data-slot=field-content]]:items-start *:data-[slot=field-label]:flex-auto has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px',
			responsive:
				'flex-col *:w-full @md/field-group:flex-row @md/field-group:items-center @md/field-group:*:w-auto @md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:*:data-[slot=field-label]:flex-auto [&>.sr-only]:w-auto @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px',
		},
	},
	defaultVariants: {
		orientation: 'vertical',
	},
});

function Field({
	className,
	orientation = 'vertical',
	...props
}: React.ComponentProps<'div'> & VariantProps<typeof fieldVariants>) {
	// Theme
	const rootClassName = cn(fieldVariants({ orientation }), className);

	// Render
	return <div role='group' data-slot='field' data-orientation={orientation} className={rootClassName} {...props} />;
}

function FieldContent({ className, ...props }: React.ComponentProps<'div'>) {
	// Theme
	const rootClassName = cn('group/field-content flex flex-1 flex-col gap-0.5 leading-snug', className);

	// Render
	return <div data-slot='field-content' className={rootClassName} {...props} />;
}

function FieldLabel({ className, ...props }: React.ComponentProps<typeof Label>) {
	// Theme
	const rootClassName = cn(
		'group/field-label peer/field-label flex w-fit gap-2 leading-snug group-data-[disabled=true]/field:opacity-50 has-data-checked:border-action/30 has-data-checked:bg-action/5 has-[>[data-slot=field]]:rounded-media has-[>[data-slot=field]]:border *:data-[slot=field]:p-2.5 dark:has-data-checked:border-action/20 dark:has-data-checked:bg-action/10',
		'has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col',
		className,
	);

	// Render
	return <Label data-slot='field-label' className={rootClassName} {...props} />;
}

function FieldTitle({ className, ...props }: React.ComponentProps<'div'>) {
	// Theme
	const rootClassName = cn(
		'flex w-fit items-center gap-2 text-sm font-normal text-foreground-body group-data-[disabled=true]/field:opacity-50',
		className,
	);

	// Render
	return <div data-slot='field-label' className={rootClassName} {...props} />;
}

function FieldDescription({ className, ...props }: React.ComponentProps<'p'>) {
	// Theme
	const rootClassName = cn(
		'text-left text-sm leading-normal font-normal text-foreground-body group-has-data-horizontal/field:text-balance [[data-variant=legend]+&]:-mt-1.5',
		'last:mt-0 nth-last-2:-mt-1',
		'[&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-action',
		className,
	);

	// Render
	return <p data-slot='field-description' className={rootClassName} {...props} />;
}

function FieldSeparator({
	children,
	className,
	...props
}: React.ComponentProps<'div'> & {
	children?: React.ReactNode;
}) {
	// Theme
	const rootClassName = cn('relative -my-2 h-5 text-sm group-data-[variant=outline]/field-group:-mb-2', className);

	// Render
	return (
		<div data-slot='field-separator' data-content={!!children} className={rootClassName} {...props}>
			<Separator className='absolute inset-0 top-1/2' />
			{children && (
				<span
					className='relative mx-auto block w-fit bg-canvas px-2 text-foreground-body'
					data-slot='field-separator-content'>
					{children}
				</span>
			)}
		</div>
	);
}

function FieldError({
	className,
	children,
	errors,
	...props
}: React.ComponentProps<'div'> & {
	errors?: Array<{ message?: string } | undefined>;
}) {
	const content = useMemo(() => {
		if (children) {
			return children;
		}

		if (!errors?.length) {
			return null;
		}

		const uniqueErrors = [...new Map(errors.map(error => [error?.message, error])).values()];

		if (uniqueErrors?.length === 1) {
			return uniqueErrors[0]?.message;
		}

		return (
			<ul className='ml-4 flex list-disc flex-col gap-1'>
				{uniqueErrors.map((error, index) => error?.message && <li key={index}>{error.message}</li>)}
			</ul>
		);
	}, [children, errors]);

	// Theme
	const rootClassName = cn('text-sm font-normal text-danger', className);

	// Render
	if (!content) {
		return null;
	}

	return (
		<div role='alert' data-slot='field-error' className={rootClassName} {...props}>
			{content}
		</div>
	);
}

export {
	Field,
	FieldLabel,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLegend,
	FieldSeparator,
	FieldSet,
	FieldContent,
	FieldTitle,
};
